// Compliance & Data Governance dashboard — a read-only, platform-gated overview of
// the data lifecycle: legal holds, erasure/anonymization requests, retention
// dry-runs, and audit evidence. Everything here is informational: nothing on this
// page deletes, anonymizes, or otherwise mutates data. Destructive retention
// enforcement is DRY-RUN only (see DryRunBanner), and this view simply reflects the
// counters and recent activity assembled by the compliance-service.
import { fmtDate, fmtDateTime } from '../../lib/format';
import React, { useEffect, useState } from 'react';
import {
  Card, Row, Col, Statistic, Table, Space, Typography, Empty, Spin, message,
} from 'antd';
import {
  SafetyCertificateOutlined, DeleteRowOutlined, AuditOutlined,
  ExperimentOutlined, LockOutlined, ClusterOutlined, TeamOutlined,
  FileSearchOutlined,
} from '@ant-design/icons';
import { DryRunBanner, ComplianceStatusBadge, LegalHoldBadge } from '@/components/compliance';
import { useAppSelector } from '@/app/hooks';
import { selectHasPermission } from '@/features/auth/authSlice';
import {
  getDashboard,
  type DashboardSummary,
  type ErasureRequest,
  type LegalHold,
  type DryRunResult,
  type AuditEvidence,
} from '@/services/dataGovernanceService';

const { Title, Text } = Typography;

const fmt = (s?: string | null) => (s ? fmtDateTime(s) : '—');
const shortId = (id?: string | null) => (id ? id.slice(0, 8) : '—');

const DataGovernanceDashboardPage: React.FC = () => {
  // The route already guards this page; this permission only tailors optional detail.
  const canView = useAppSelector(selectHasPermission('compliance:dashboard:view'));

  const [data, setData] = useState<DashboardSummary | null>(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const d = await getDashboard();
      setData(d);
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Could not load the compliance dashboard.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const stats: { title: string; value: number; icon: React.ReactNode }[] = [
    { title: 'Active Legal Holds', value: data?.active_legal_holds ?? 0, icon: <LockOutlined /> },
    { title: 'Open Erasure Requests', value: data?.open_erasure_requests ?? 0, icon: <DeleteRowOutlined /> },
    { title: 'Pending Review Requests', value: data?.pending_review_requests ?? 0, icon: <FileSearchOutlined /> },
    { title: 'Retention Dry-Run Findings', value: data?.dry_run_findings ?? 0, icon: <ExperimentOutlined /> },
    { title: 'Records Blocked by Legal Hold', value: data?.records_blocked_by_legal_hold ?? 0, icon: <SafetyCertificateOutlined /> },
    { title: 'Completed Anonymization Actions', value: data?.completed_anonymizations ?? 0, icon: <AuditOutlined /> },
    { title: 'Tenants in Offboarding', value: data?.tenants_in_offboarding ?? 0, icon: <ClusterOutlined /> },
    { title: 'Employees Pending Retention Review', value: data?.employees_pending_retention_review ?? 0, icon: <TeamOutlined /> },
  ];

  const erasureColumns = [
    { title: 'Employee', dataIndex: 'employee_id', key: 'employee_id', render: (v: string) => <Text code>{shortId(v)}</Text> },
    { title: 'Type', dataIndex: 'request_type', key: 'request_type', render: (v: string) => (v || '—').replace(/_/g, ' ') },
    { title: 'Status', dataIndex: 'status', key: 'status', render: (v: string) => <ComplianceStatusBadge kind="erasure" value={v} /> },
    { title: 'Requested', dataIndex: 'requested_at', key: 'requested_at', render: fmt },
  ];

  const legalHoldColumns = [
    { title: 'Subject', dataIndex: 'subject_type', key: 'subject_type', render: (v: string) => (v || '—').replace(/_/g, ' ') },
    { title: 'Reason', dataIndex: 'reason', key: 'reason', ellipsis: true, render: (v: string) => v || '—' },
    { title: 'Status', dataIndex: 'status', key: 'status', render: (v: string) => <LegalHoldBadge active={v === 'ACTIVE'} /> },
    { title: 'Created', dataIndex: 'created_at', key: 'created_at', render: fmt },
  ];

  const dryRunColumns = [
    { title: 'Service', dataIndex: 'service_name', key: 'service_name' },
    { title: 'Record Type', dataIndex: 'record_type', key: 'record_type' },
    { title: 'Proposed Action', dataIndex: 'proposed_action', key: 'proposed_action', render: (v: string) => (v || '—').replace(/_/g, ' ') },
    { title: 'Decision', dataIndex: 'decision', key: 'decision', render: (v: string) => <ComplianceStatusBadge kind="dry_run_decision" value={v} /> },
    { title: 'Evaluated', dataIndex: 'evaluated_at', key: 'evaluated_at', render: fmt },
  ];

  const evidenceColumns = [
    { title: 'Action', dataIndex: 'action', key: 'action', render: (v: string) => <ComplianceStatusBadge kind="evidence_action" value={v} /> },
    { title: 'Service', dataIndex: 'service_name', key: 'service_name' },
    { title: 'Record Type', dataIndex: 'record_type', key: 'record_type' },
    { title: 'Performed', dataIndex: 'performed_at', key: 'performed_at', render: fmt },
  ];

  const widget = (
    title: string,
    node: React.ReactNode,
  ) => (
    <Card title={title} size="small" style={{ height: '100%' }}>
      {node}
    </Card>
  );

  const erasures = data?.recent_erasure_requests ?? [];
  const holds = data?.recent_legal_holds ?? [];
  const dryRuns = data?.latest_dry_run ?? [];
  const evidence = data?.recent_audit_evidence ?? [];

  return (
    <div className="p-1">
      <Space align="center" style={{ marginBottom: 4 }}>
        <SafetyCertificateOutlined style={{ fontSize: 22 }} />
        <Title level={3} style={{ margin: 0 }}>Compliance &amp; Data Governance</Title>
      </Space>
      <div>
        <Text type="secondary">Data lifecycle overview</Text>
      </div>

      <div style={{ margin: '16px 0' }}>
        <DryRunBanner />
      </div>

      <Spin spinning={loading}>
        <Row gutter={[16, 16]}>
          {stats.map((s) => (
            <Col xs={24} sm={12} lg={6} key={s.title}>
              <Card>
                <Statistic title={s.title} value={s.value} prefix={s.icon} />
              </Card>
            </Col>
          ))}
        </Row>
      </Spin>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} lg={12}>
          {widget(
            'Recent Erasure Requests',
            erasures.length === 0 && !loading ? (
              <Empty description="No recent erasure requests" />
            ) : (
              <Table<ErasureRequest>
                rowKey="id"
                size="small"
                loading={loading}
                dataSource={erasures}
                columns={erasureColumns as any}
                pagination={false}
              />
            ),
          )}
        </Col>

        <Col xs={24} lg={12}>
          {widget(
            'Recent Legal Holds',
            holds.length === 0 && !loading ? (
              <Empty description="No recent legal holds" />
            ) : (
              <Table<LegalHold>
                rowKey="id"
                size="small"
                loading={loading}
                dataSource={holds}
                columns={legalHoldColumns as any}
                pagination={false}
              />
            ),
          )}
        </Col>

        <Col xs={24} lg={12}>
          {widget(
            'Latest Retention Dry-Run Results',
            dryRuns.length === 0 && !loading ? (
              <Empty description="No retention dry-run results yet" />
            ) : (
              <Table<DryRunResult>
                rowKey="id"
                size="small"
                loading={loading}
                dataSource={dryRuns}
                columns={dryRunColumns as any}
                pagination={false}
              />
            ),
          )}
        </Col>

        <Col xs={24} lg={12}>
          {widget(
            'Recent Audit Evidence Events',
            evidence.length === 0 && !loading ? (
              <Empty description="No recent audit evidence events" />
            ) : (
              <Table<AuditEvidence>
                rowKey="id"
                size="small"
                loading={loading}
                dataSource={evidence}
                columns={evidenceColumns as any}
                pagination={false}
              />
            ),
          )}
        </Col>
      </Row>

      {canView && data && !data.destructive_enforcement_enabled && (
        <div style={{ marginTop: 16 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            This dashboard is read-only. Destructive retention enforcement is disabled;
            all retention evaluations shown here are dry-run previews only.
          </Text>
        </div>
      )}
    </div>
  );
};

export default DataGovernanceDashboardPage;
